import {
	Button,
	DataTable,
	Alert,
	Typography,
	Avatar,
	Dialog,
	BDSProvider,
	IconButton,
} from '@barracuda-internal/bds-core-v2';
import {
	DialogContent,
	DialogTitle,
	DialogActions,
	List,
	ListItem,
	Box,
} from '@mui/material';
import Delete from '@barracuda-internal/bds-core-v2/dist/Icons/Core/Delete';
import { MRT_ColumnDef } from 'material-react-table';
import React, { ReactElement, useEffect, useMemo, useState } from 'react';
import FEATURES from 'src/featureMap';
import { useAppToolchain } from 'src/hooks/useAppToolchain';
import useAPIEffect from 'src/lib/useAPIEffect';
import withAnalytics from 'src/lib/withAnalytics';
import { ClassifierTypes, ClassifierView } from 'src/types/dataTypes';
import ulog from 'ulog';
import classifierMap from './classifierMap';
import useToast from 'src/hooks/useToast';
import ToastComponent from 'src/components/toast/ToastComponent';

export interface ClassifierTableProps {
	accountId: string;
}

function ClassifierTable({ accountId }: ClassifierTableProps): ReactElement {
	const { api } = useAppToolchain();
	const { toasts, triggerToast } = useToast();

	const [enabledTypes, setEnabledTypes] = useState([]);
	const [isDirty, setIsDirty] = useState(false);
	const [showStore, setShowStore] = useState(false);
	const [limit, setLimit] = useState({});
	const [classifiers, setClassifiers]: [
		ClassifierView[],
		React.Dispatch<ClassifierView[]>,
	] = useState([]);

	const [createClassifier, setCreateClassifier]: [
		ClassifierTypes,
		React.Dispatch<ClassifierTypes>,
	] = useState();

	// The classifier that we are currently editing.
	// Will display in a modal.
	const [editingClassifier, setEditingClassifier]: [
		ClassifierView,
		React.Dispatch<ClassifierView>,
	] = useState();

	const [loadingClassifiers, setLoadingClassifiers] = useState(false);

	useAPIEffect(
		(cancel) => {
			if (cancel) return;

			setLoadingClassifiers(true);
			Promise.all([
				api.getAccountClassifierViews(accountId),
				api.getAccountClassifierTypes(accountId),
			])
				.then((results) => {
					ulog.debug('ClassifierGroup:', results);
					setClassifiers(results[0]);
					setEnabledTypes(results[1]);
					let limits = {};
					results[1].forEach((type) => {
						limits[type.type] = {
							max: type.restrictedCount,
							current: type.count,
						};
					});
					setLimit(limits);
					Object.entries(limits).map(([key, val]) => {
						if (limits[key].current >= limits[key].max) {
							limits[key].disable = true;
						} else {
							limits[key].disable = false;
						}
					});
					setIsDirty(false);
				})
				.catch((err) => {
					ulog.error('Error loading classifiers:', err);
				})
				.finally(() => {
					setLoadingClassifiers(false);
				});
		},
		[isDirty],
	);

	useAPIEffect(
		(cancel) => {
			if (cancel || !createClassifier) return;
			let obj: ClassifierView = {
				accountId: accountId,
				created: '',
				disabled: true,
				displayName: '',
				id: '',
				tagName: '',
				type: createClassifier,
				payload: undefined,
				configUrl: '',
				current: limit[createClassifier].current,
				max: limit[createClassifier].max,
			};

			setEditingClassifier(obj);
			setShowStore(false);
			setCreateClassifier(null);
		},
		[createClassifier],
	);

	const EditDialog =
		!!editingClassifier && classifierMap[editingClassifier.type]
			? classifierMap[editingClassifier.type].dialog
			: null;

	const handleClose = () => setEditingClassifier(undefined);
	const handleSave = () => setIsDirty(true);

	const filteredData = useMemo(() => {
		return classifiers.filter(
			(c) =>
				enabledTypes.some((t) => t.type === c.type) &&
				!!classifierMap[c.type],
		);
	}, [classifiers, enabledTypes]);

	const columns = useMemo<MRT_ColumnDef<ClassifierView>[]>(
		() => [
			{
				id: 'typeIcon',
				header: ' ',
				size: 100,
				enableSorting: false,
				Cell: ({ row }) => (
					<Avatar>
						{(() => {
							const Icon = classifierMap[row.original.type]?.icon;
							return Icon ? <Icon /> : null;
						})()}
					</Avatar>
				),
			},
			{
				accessorKey: 'type',
				id: 'typeName',
				header: 'Type',
				Cell: ({ row }) => (
					<Typography variant="body1" color="textPrimary">
						{classifierMap[row.original.type]?.name}
					</Typography>
				),
			},
			{
				accessorKey: 'displayName',
				header: 'Name',
			},
			{
				accessorKey: 'tagName',
				header: 'Label Name',
				Cell: ({ cell }) => (
					<Typography variant="body1" color="textPrimary">
						{cell.getValue<string>() || 'Not configured yet'}
					</Typography>
				),
			},
			{
				accessorKey: 'disabled',
				header: 'Status',
				Cell: ({ cell }) => (
					<Typography variant="body1" color="textPrimary">
						{cell.getValue<boolean>() ? 'Disabled' : 'Enabled'}
					</Typography>
				),
			},
			{
				accessorKey: 'id',
				id: 'editAction',
				header: ' ',
				size: 100,
				enableSorting: false,
				Cell: ({ row }) => (
					<Button
						variant="contained"
						color="primary"
						onClick={withAnalytics(
							FEATURES.settingsClassifierEditButtonClick,
							() => setEditingClassifier(row.original),
							{
								type: row.original.type,
								disabled: row.original.disabled,
							},
						)}
					>
						Edit
					</Button>
				),
			},
			{
				accessorKey: 'id',
				id: 'deleteAction',
				header: ' ',
				size: 150,
				enableSorting: false,
				Cell: ({ row }) => (
					<Box sx={{ display: 'flex', justifyContent: 'center' }}>
						<DeleteCell
							classifier={row.original}
							onDelete={() => {
								setIsDirty(true);
							}}
							triggerToast={triggerToast}
						/>
					</Box>
				),
			},
		],
		[setEditingClassifier, setIsDirty],
	);

	return (
		<BDSProvider>
			<Box sx={{ padding: 1, textAlign: 'right' }}>
				<Button
					onClick={() => setShowStore(true)}
					color="primary"
					variant="contained"
				>
					Add Classifier
				</Button>
			</Box>
			{limit['CustomRegexFindingView'] &&
			limit['CustomRegexFindingView'].current >=
				limit['CustomRegexFindingView'].max ? (
				<>
					<Alert color="info">
						Note: You have reached the limit of 10 Regular Expression
						classifiers. To add a new one, please delete or modify an
						existing classifier.
					</Alert>
					<br />
				</>
			) : null}

			<DataTable
				columns={columns}
				data={filteredData}
				enableTopToolbar={false}
				enableBottomToolbar={false}
				enablePagination={false}
				enableColumnActions={false}
				enableColumnFilters={false}
				enableSorting={false}
				enableGlobalFilter={false}
				showSearchFields={false}
				state={{
					isLoading: loadingClassifiers,
				}}
			/>
			<Dialog open={showStore} onClose={() => setShowStore(false)}>
				<DialogTitle>
					<Typography component="span" variant="h3" color="textPrimary">
						What kind of classifier would you like to create?
					</Typography>
				</DialogTitle>
				<DialogContent>
					<List>
						{Object.entries(classifierMap).map(([key, val]) => {
							if (limit[key] && !limit[key].disable) {
								return (
									<ListItem
										component="button"
										key={key}
										onClick={withAnalytics(
											FEATURES.settingsClassifierAddClassifierClick,
											() =>
												setCreateClassifier(
													key as ClassifierTypes,
												),
											{
												classifier: key,
											},
										)}
										sx={{ cursor: 'pointer', background: 'none', border: 'none', '&:hover': { backgroundColor: 'rgba(0,0,0,0.04)' } }}
									>
										<Typography variant="body1" color="textPrimary">
											{val.name}{' '}
											{limit[key] && limit[key].max < 1000 ? (
												<Typography
													component="span"
													variant="body1"
													color="textSecondary"
												>
													{limit[key].current} of {limit[key].max}
												</Typography>
											) : null}
										</Typography>
									</ListItem>
								);
							} else {
								return (
									<ListItem key={key} sx={{ background: 'none', border: 'none' }}>
										<Typography variant="body1" color="textSecondary">
											{val.name}{' '}
											{limit[key] && limit[key].max < 1000 ? (
												<Typography
													component="span"
													variant="body1"
													color="textSecondary"
												>
													{limit[key].current} of {limit[key].max}
												</Typography>
											) : null}
										</Typography>
									</ListItem>
								);
							}
						})}
					</List>
				</DialogContent>
			</Dialog>
			<Dialog
				maxWidth="sm"
				open={!!editingClassifier}
				onClose={() => setEditingClassifier(undefined)}
			>
				{!!editingClassifier ? (
					<EditDialog
						accountId={accountId}
						baseClassifier={editingClassifier}
						onClose={handleClose}
						onSave={handleSave}
					/>
				) : null}
			</Dialog>
			<ToastComponent toasts={toasts} position={'bottom-left'} />
		</BDSProvider>
	);
}

export function DeleteCell({ classifier, onDelete, triggerToast }) {
	const { api } = useAppToolchain();

	const [showDialog, setShowDialog] = useState(false);
	const [doDelete, setDoDelete] = useState(false);
	const [isDeleting, setIsDeleting] = useState(false);

	const handleDeleteClick = () => {
		setShowDialog(true);
	};

	const handleConfirmDelete = () => {
		setDoDelete(true);
		setShowDialog(false);
	};

	const handleCancelDelete = () => {
		setShowDialog(false);
	};

	useAPIEffect(
		(cancel, signal) => {
			if (!doDelete) return;

			setIsDeleting(true);
			api.del(classifier.configUrl, null, null, false)
				.then(() => {
					if (cancel) return;
					triggerToast({
						type: 'success',
						message: `Classifier "${classifier.displayName}" deleted successfully`,
						duration: 3000,
					});
					onDelete(classifier);
				})
				.catch(() => {
					if (cancel) return;
					triggerToast({
						type: 'error',
						message: `Failed to delete classifier "${classifier.displayName}"`,
						duration: 3000,
					});
				})
				.finally(() => {
					if (cancel) return;
					setIsDeleting(false);
					setDoDelete(false);
				});
		},
		[doDelete],
	);

	return (
		<>
			<IconButton
				color="error"
				onClick={withAnalytics(
					FEATURES.settingsClassifierDeleteFirstClick,
					handleDeleteClick,
					{ classifier: classifier.type },
				)}
				disabled={isDeleting}
				aria-label="delete"
			>
				<Delete />
			</IconButton>
			<Dialog open={showDialog} onClose={handleCancelDelete}>
				<DialogTitle>
					<Typography component="span" variant="h3" color="textPrimary">
						Delete Classifier
					</Typography>
				</DialogTitle>
				<DialogContent>
					<Typography variant="body1" color="textPrimary">
						Do you want to delete "{classifier.displayName}" permanently?
					</Typography>
				</DialogContent>
				<DialogActions sx={{ justifyContent: 'flex-end', padding: 2 }}>
					<Button
						bdsType="interactiveNeutral"
						onClick={handleCancelDelete}
					>
						No
					</Button>
					<Button
						bdsType="interactiveEmphasis"
						onClick={withAnalytics(
							FEATURES.settingsClassifierDeleteSecondClick,
							handleConfirmDelete,
							{ classifier: classifier.type },
						)}
					>
						Yes
					</Button>
				</DialogActions>
			</Dialog>
		</>
	);
}

export default ClassifierTable;
